import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseSuggestedConnectionsParams {
  userId: string | undefined;
  profileCompleted: boolean;
  hasCompletedSuggestedConnections: boolean;
}

interface UseSuggestedConnectionsReturn {
  showSuggestedConnections: boolean;
  closeSuggestedConnections: () => void;
}

export function useSuggestedConnections({
  userId,
  profileCompleted,
  hasCompletedSuggestedConnections,
}: UseSuggestedConnectionsParams): UseSuggestedConnectionsReturn {
  const [showSuggestedConnections, setShowSuggestedConnections] = useState(false);

  useEffect(() => {
    const shouldShow =
      !!userId && profileCompleted && !hasCompletedSuggestedConnections;
    setShowSuggestedConnections(shouldShow);
  }, [userId, profileCompleted, hasCompletedSuggestedConnections]);

  const closeSuggestedConnections = useCallback(() => {
    setShowSuggestedConnections(false);
  }, []);

  return {
    showSuggestedConnections,
    closeSuggestedConnections,
  };
}

interface UseProfileCompletionTriggerReturn {
  shouldShowSuggested: boolean;
  triggerAfterProfileCompletion: () => Promise<void>;
  resetTrigger: () => void;
}

export function useProfileCompletionTrigger(
  userId: string | undefined
): UseProfileCompletionTriggerReturn {
  const [shouldShowSuggested, setShouldShowSuggested] = useState(false);

  const triggerAfterProfileCompletion = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("has_completed_suggested_connections")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error checking suggested connections status:", error);
        return;
      }

      if (data && !data.has_completed_suggested_connections) {
        setShouldShowSuggested(true);
      }
    } catch (err) {
      console.error("Error in triggerAfterProfileCompletion:", err);
    }
  }, [userId]);

  const resetTrigger = useCallback(() => {
    setShouldShowSuggested(false);
  }, []);

  return {
    shouldShowSuggested,
    triggerAfterProfileCompletion,
    resetTrigger,
  };
}

interface UseLowConnectionsTriggerParams {
  userId: string | undefined;
  activeConnectionCount: number | undefined;
  hasCompletedSuggestedConnections: boolean | undefined;
}

interface UseLowConnectionsTriggerReturn {
  shouldShow: boolean;
  dismiss: () => void;
}

export function useLowConnectionsTrigger({
  userId,
  activeConnectionCount,
  hasCompletedSuggestedConnections,
}: UseLowConnectionsTriggerParams): UseLowConnectionsTriggerReturn {
  const [shouldShow, setShouldShow] = useState(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only check once per session to avoid repeat popups
    if (hasCheckedRef.current) return;
    if (!userId) return;
    if (activeConnectionCount === undefined) return;
    if (hasCompletedSuggestedConnections === undefined) return;

    const shouldTrigger =
      activeConnectionCount < 2 && !hasCompletedSuggestedConnections;

    if (shouldTrigger) {
      setShouldShow(true);
    }
    hasCheckedRef.current = true;
  }, [userId, activeConnectionCount, hasCompletedSuggestedConnections]);

  const dismiss = useCallback(() => {
    setShouldShow(false);
  }, []);

  return {
    shouldShow,
    dismiss,
  };
}
